/**
 * Shared call event insertion service (Audit Finding #14).
 * Extracts insertCallEventMessage from useElefaCall.ts for reuse.
 */

import { supabase } from '@/integrations/supabase/client';

/** Typed payload for call events (Audit Finding #12) */
export interface CallEventPayload {
  call_id: string;
  is_video: boolean;
  is_missed: boolean;
  duration: number;
  status: 'completed' | 'missed' | 'declined' | 'failed' | 'rejected';
}

/** Typed content stored inside chat_messages for call events */
export interface CallEventContent {
  is_video: boolean;
  is_missed: boolean;
  duration: number;
  caller_id: string;
  status: string;
}

/**
 * Insert a call event message into the shared chat room.
 * Signal-style: missed/rejected/completed calls appear as messages in chat.
 * Idempotent: uses call_id in metadata to prevent duplicates.
 */
export async function insertCallEventMessage(
  senderId: string,
  callerId: string,
  calleeId: string,
  callMeta: CallEventPayload,
): Promise<void> {
  try {
    const otherUserId = senderId === callerId ? calleeId : callerId;

    const { data: myRooms } = await supabase
      .from('chat_room_members')
      .select('room_id')
      .eq('user_id', senderId);

    if (!myRooms || myRooms.length === 0) return;

    const myRoomIds = myRooms.map(r => r.room_id);

    const { data: sharedMembership } = await supabase
      .from('chat_room_members')
      .select('room_id')
      .eq('user_id', otherUserId)
      .in('room_id', myRoomIds)
      .limit(1);

    const roomId = sharedMembership?.[0]?.room_id;
    if (!roomId) return;

    // Idempotency check
    const { data: existing } = await supabase
      .from('chat_messages')
      .select('id')
      .eq('room_id', roomId)
      .eq('message_type', 'call_event')
      .contains('metadata', { call_id: callMeta.call_id } as Record<string, unknown>)
      .limit(1);

    if (existing && existing.length > 0) return;

    const content: CallEventContent = {
      is_video: callMeta.is_video,
      is_missed: callMeta.is_missed,
      duration: callMeta.duration,
      caller_id: callerId,
      status: callMeta.status,
    };

    await supabase.from('chat_messages').insert({
      room_id: roomId,
      sender_id: callerId,
      content: JSON.stringify(content),
      message_type: 'call_event',
      is_encrypted: false,
      metadata: { call_id: callMeta.call_id },
    });
  } catch (err) {
    console.warn('[CallEvents] Failed to insert call event message:', err);
  }
}

/**
 * Load early ICE candidates for a call (Audit Finding #15).
 * Shared between answerCall and processAnswerSdp.
 */
export async function loadEarlyIceCandidates(
  callId: string,
  myUserId: string,
  sharedSecret: CryptoKey | null,
  pc: RTCPeerConnection,
  decryptSignaling: (content: string, secret: CryptoKey | null) => Promise<string>,
): Promise<void> {
  const { data: earlyCandidates } = await supabase
    .from('elefa_call_ice_candidates')
    .select('candidate, sender_id')
    .eq('call_id', callId)
    .neq('sender_id', myUserId);

  if (!earlyCandidates) return;

  for (const row of earlyCandidates) {
    try {
      const decrypted = await decryptSignaling(row.candidate, sharedSecret);
      const candidate = JSON.parse(decrypted);
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch { /* skip bad candidate */ }
  }
}
